import "./login.scss";
import type { FC } from "react";
import * as React from "react";
import { DialogType, useDialogCreate } from "@onshopfront/components";
import moment from "moment";
import { type ShopfrontDispatch, useShopfrontDispatch, useShopfrontSelector } from "#/_Bootstrap/hooks.ts";
import { determineDataSource } from "#/Data/DataStores/DataSource.ts";
import { UpdatePasswordDialog } from "#/Routes/Settings/Users/UpdatePasswordDialog.tsx";
import { isKeyOf } from "../../../types/internal/utility";
import { history } from "../../_Bootstrap/RoutingAndStore";
import { firstView } from "../../Actions/Information/InformationActions";
import {
    type AuthenticatedViewer,
    getAuthenticatedViewer,
    userLoaded,
} from "../../Actions/Users/UserActions";
import Button from "../../Common/Buttons/Button";
import ShopfrontLogo from "../../Common/Misc/ShopfrontLogo";
import PopupManager from "../../Common/Popups/PopupManager";
import { UNIQUE_PAGES } from "../../Common/UniquePage/UniquePage";
import AutoLogout from "../../Utilities/Domain/Users/AutoLogout";
import { getURLQuery } from "../../Utilities/Platform/CurrentRequest";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { getLogLevel, LogLevel } from "../../Utilities/Platform/Logging";
import { getOption, setOption } from "../../Utilities/Platform/Options/Options";
import { arePoliciesLoaded, getPolicy, loadEnterprisePolicies } from "../../Utilities/Platform/Policies/Policies";
import Request, { forceOfflineRequests } from "../../Utilities/Platform/Request";
import {
    disableResponse,
    enableResponse,
    getOpenPages,
    pingTabs,
} from "../../Workers/Tab/TabEvents";
import FocusLost from "../SellScreen/Dialogs/FocusLost";
import NotFoundException from "../SellScreen/Exceptions/Data/NotFoundException";
import { EnterpriseLoginBox } from "./EnterpriseLoginBox";
import LoginBox from "./LoginBox";

interface Props {
    first: boolean;
    focus: boolean;
    dispatch: ShopfrontDispatch;
    createDialog: ReturnType<typeof useDialogCreate>;
}

type LoginUserType = {
    id: string;
    username: string;
    name: string;
    image: string;
};

type State = {
    loading: boolean;
    mode: "type" | "click";
    password: boolean;
    error: boolean | string;
    page: false | string;
    users: Array<LoginUserType>;
    override: boolean;
    enterpriseLoginVisible: boolean;
};

function removeQueryKey(query: ReturnType<typeof getURLQuery>, remove: string): void {
    // Replace url history record to not have remove key
    const search = [];

    for(const key in query) {
        if(Object.prototype.hasOwnProperty.call(query, key)) {
            const value = query[key];

            if(key !== remove) {
                const uriKey = encodeURIComponent(key);

                if(Array.isArray(value)) {
                    for(let i = 0, l = value.length; i < l; i++) {
                        const arrayValue = value[i];

                        if(arrayValue !== null) {
                            search.push(`${uriKey}[]=${encodeURIComponent((arrayValue as string).toString())}`);
                        } else {
                            search.push(`${uriKey}[]=null`);
                        }
                    }
                } else if(value !== null) {
                    search.push(uriKey + "=" + encodeURIComponent(value.toString()));
                } else {
                    search.push(`${uriKey}=null`);
                }
            }
        }
    }

    let searchString = "";

    if(search.length) {
        searchString = "?" + search.join("&");
    }

    window.history.replaceState(null, "", window.location.pathname + searchString);
}

function getSignInType(): "type" | "click" {
    const signInType = getOption("sign_in_type");

    if(signInType === "click") {
        return "click";
    }

    return "type";
}

export class LoginScreenRenderer extends React.Component<Props, State> {
    public pageChecking = false;
    public pageInterval: null | ReturnType<typeof setInterval> = null;

    constructor(props: Readonly<Props>) {
        super(props);

        const requirePassword = this.isFirstVisit() ||
            getOption("sign_in_require_password");

        this.state = {
            loading               : true,
            mode                  : getSignInType(),
            password              : requirePassword,
            error                 : false,
            page                  : false,
            users                 : [],
            override              : false,
            enterpriseLoginVisible: getPolicy("enterprise_login_visible", false),
        };

        this.loginCurrentUser = this.loginCurrentUser.bind(this);
        this.loginOfflineUser = this.loginOfflineUser.bind(this);
        this.prepareLogin = this.prepareLogin.bind(this);
        this.handleDashboardClick = this.handleDashboardClick.bind(this);
        this.handleAttemptOpenPage = this.handleAttemptOpenPage.bind(this);
        this.handleOverrideCheck = this.handleOverrideCheck.bind(this);
    }

    public componentDidMount(): void {
        this.setState({
            loading: true,
        });

        void this.prepareLogin();

        window.addEventListener("keydown", this.handleOverrideCheck);

        if(!arePoliciesLoaded()) {
            void this.loadEnterprisePolicies();
        }
    }

    public componentWillUnmount(): void {
        window.removeEventListener("keydown", this.handleOverrideCheck);
    }

    protected loadEnterprisePolicies(): Promise<void> {
        return loadEnterprisePolicies()
            .then(() => {
                this.setState({
                    enterpriseLoginVisible: getPolicy("enterprise_login_visible", false),
                });
            })
            .catch(err => {
                console.error("We could not load the enterprise policies", err);
            });
    }

    public handleOverrideCheck = (e: KeyboardEvent): void => {
        if(e.altKey && e.ctrlKey && e.shiftKey && e.code === "KeyS") {
            this.setState(prevState => ({
                override: !prevState.override,
            }));
        }
    };

    public prepareLogin = (): Promise<void> => {
        if(process.env.NODE_ENV !== "test") {
            performance.mark("SF-LOGIN-START");
        }

        const query = getURLQuery();

        // Check if they came from global login
        let global: boolean | string = false;

        if(query.globallogin) {
            global = query.globallogin as string;

            removeQueryKey(query, "globallogin");
        }

        if(query.adminlogin) {
            removeQueryKey(query, "adminlogin");

            return this.loginCurrentUser();
        }

        if(process.env.NODE_ENV !== "test") {
            performance.mark("SF-LOGIN-ISGLOBAL-START");
        }

        return this.handleGlobalLogin(global)
            .then(global => {
                if(process.env.NODE_ENV !== "test") {
                    performance.mark("SF-LOGIN-ISGLOBAL-END");
                    performance.measure(
                        "Shopfront Is Global Login",
                        "SF-LOGIN-ISGLOBAL-START",
                        "SF-LOGIN-ISGLOBAL-END"
                    );
                }

                if(global) {
                    if(window.location.pathname === "/login") {
                        history.replace("/");
                    }

                    return this.loginCurrentUser();
                } else {
                    return this.determineLoginMethod();
                }
            })
            .then(() => {
                if(process.env.NODE_ENV !== "test") {
                    performance.mark("SF-LOGIN-END");
                    performance.measure("Shopfront Login", "SF-LOGIN-START", "SF-LOGIN-END");
                }
            });
    };

    public handleDashboardClick = (): void => {
        history.push("/dashboard");

        if(typeof window !== "undefined") {
            window.location.reload();
        }
    };

    public handleAttemptOpenPage = (): void => {
        Notification.requestPermission()
            .then(permission => {
                if(permission === "granted") {
                    if(!this.state.page) {
                        return;
                    }

                    const page = this.state.page as keyof typeof UNIQUE_PAGES;

                    pingTabs(page, UNIQUE_PAGES[page] + " found, click here to open");
                } else if(permission === "denied") {
                    PopupManager.error("Allow notifications to find other page");
                }
            });
    };

    public loginCurrentUser = (username?: string, skipPasswordReset = false): Promise<void> => {
        // Get logged in user
        return getAuthenticatedViewer()
            .then((viewer) => {
                this.login(viewer, skipPasswordReset);
                const query = getURLQuery();

                if(query.intended) {
                    history.push(query.intended as string);
                }
            })
            .catch((err) => {
                console.error("Could not get the current user", err);

                if(
                    typeof username === "string" &&
                    err.response &&
                    err.response.status >= 500 &&
                    err.response.status < 600
                ) {
                    this.loginOfflineUser(username, true)
                        .finally(() => {
                            this.setState({
                                loading: false,
                            });
                        });
                } else {
                    this.setState({
                        loading: false,
                    });
                }
            });
    };

    public loginOfflineUser = (username: string, forced: boolean): Promise<void> => {
        // Try to login user offline
        return determineDataSource()
            .then(source => {
                return source.users.getUserByUsername(username);
            })
            .then(user => {
                if(user.is_api_user) {
                    throw new NotFoundException();
                }

                if(forced) {
                    forceOfflineRequests();
                }

                this.login({
                    id           : user.uuid,
                    name         : user.name,
                    username     : user.username,
                    image        : user.image,
                    offline      : true,
                    quickMenu    : [],
                    darkMode     : user.dark_mode,
                    passwordReset: false,
                });
            });
    };

    public login = (user: AuthenticatedViewer, skipPasswordReset = false): void => {
        setOption("last_login_time", moment().toISOString());

        AutoLogout.updateTimeEvent(Date.now());

        if(this.props.first) {
            // Clear first login
            this.props.dispatch(firstView());
        }

        // Load the user
        this.props.dispatch(userLoaded(user));
        setOption("loggedout", false);

        if(user.passwordReset && !skipPasswordReset) {
            this.props.createDialog({
                type   : DialogType.Edit,
                title  : "Password Update Required",
                content: (
                    <UpdatePasswordDialog
                        id={user.id}
                        requireUpdate
                    />
                ),
                blockClosing: true,
            });
        }
    };

    public handleGlobalLogin = (global: boolean | string): Promise<boolean> => {
        if(!global) {
            return Promise.resolve(false);
        }

        return Request.post("/globalvalidate", {
            globallogin: global,
        })
            .then(response => !!response.data.success)
            .catch(err => {
                console.error("Error validating global login", err);

                return false;
            });
    };

    public determineLoginMethod = (): Promise<void> => {
        // Check if this is first time login (through checking if any users are stored in IndexedDB)
        return this.determineAuthRequired()
            .then(checkAuth => {
                // If no users are found, ask for username and password
                if(checkAuth) {
                    // Request if the user needs to login through username and password
                    return Request.get("/authorized")
                        .then(response => {
                            const authorized = response.data.authorized;

                            // If the normal login method can be used check and see if a user is still logged in
                            if(authorized && !getOption("loggedout")) {
                                void this.loginCurrentUser(undefined, true);
                            } else {
                                this.setState((prevState) => {
                                    let mode = prevState.mode;

                                    if(!authorized && prevState.password) {
                                        mode = "type";
                                    }

                                    return {
                                        ...prevState,
                                        loading: false,
                                        mode,
                                    };
                                });
                            }
                        })
                        .catch(err => {
                            console.error("Error checking authentication", err);
                            this.setState({
                                loading : false,
                                mode    : "type",
                                password: true,
                            });
                        });
                } else {
                    const changes: Partial<State> = {
                        loading: false,
                    };

                    if(this.isFirstVisit()) {
                        // Request login through username and password
                        changes.mode = "type";
                        changes.password = true;
                    }

                    this.setState(prevState => ({
                        ...prevState,
                        ...changes,
                    }));
                }
            })
            .catch(err => {
                console.error("Could not determine whether authentication is required", err);
            });
    };

    public checkOpenPages = (): Promise<void> => {
        if(this.pageChecking || !this.pageInterval) {
            return Promise.resolve();
        }

        this.pageChecking = true;

        return getOpenPages(this.props.first)
            .catch(() => {
                return {};
            })
            .then(pages => {
                this.pageChecking = false;

                if(Object.keys(pages).length === 0) {
                    if(this.pageInterval) {
                        clearInterval(this.pageInterval);
                        this.pageInterval = null;
                    }

                    enableResponse();

                    void this.determineLoginMethod();
                }
            });
    };

    public determineAuthRequired = (): Promise<boolean> => {
        return new Promise((res, rej) => {
            // Check if there are any users in the database
            if(process.env.NODE_ENV !== "test") {
                performance.mark("SF-LOGIN-AUTHREQUIRED-USERS-START");
            }

            return determineDataSource()
                .then(source => {
                    return source.users.all();
                })
                .then(users => {
                    if(process.env.NODE_ENV !== "test") {
                        performance.mark("SF-LOGIN-AUTHREQUIRED-USERS-END");
                        performance.measure(
                            "Shopfront Auth Required Users",
                            "SF-LOGIN-AUTHREQUIRED-USERS-START",
                            "SF-LOGIN-AUTHREQUIRED-USERS-END"
                        );
                    }

                    if(!users.length) {
                        // No users exists show username/password
                        this.setState({
                            mode: "type",
                        });

                        res(false);

                        return;
                    }

                    // Format users
                    const userList: Array<LoginUserType> = users.filter(user => !user.is_api_user)
                        .map((user) => ({
                            id      : user.uuid,
                            username: user.username,
                            name    : user.name,
                            image   : user.image ? user.image : "",
                        }));

                    // Sort users
                    userList.sort((a, b) => {
                        return a.name.localeCompare(b.name);
                    });

                    // Format users and store
                    this.setState({
                        users: userList,
                    });

                    // Request open pages
                    if(process.env.NODE_ENV !== "test") {
                        performance.mark("SF-LOGIN-AUTHREQUIRED-OPENPAGES-START");
                    }

                    const openPageStart = performance.now();

                    return getOpenPages(this.props.first)
                        .catch(err => {
                            // Allow timeout to continue
                            if(err !== "Getting open pages timed out") {
                                return Promise.reject(err);
                            }

                            return {};
                        })
                        .then(pages => {
                            if(process.env.NODE_ENV !== "test") {
                                performance.mark("SF-LOGIN-AUTHREQUIRED-OPENPAGES-END");
                                performance.measure(
                                    "Shopfront Auth Required Open Pages",
                                    "SF-LOGIN-AUTHREQUIRED-OPENPAGES-START",
                                    "SF-LOGIN-AUTHREQUIRED-OPENPAGES-END"
                                );
                            }

                            const openPageEnd = performance.now();

                            if(process.env.NODE_ENV !== "test" && getLogLevel() >= LogLevel.NORMAL) {
                                console.debug(`Finished getting the open pages. Time taken ${(openPageEnd - openPageStart) / 1000} seconds`);
                            }

                            // Check if first page open
                            const openPaths = Object.keys(pages);

                            if(!openPaths.length) {
                                res(this.props.first);
                            } else if(this.props.first && typeof window.location !== "undefined") {
                                const page = window.location.pathname;

                                // Check if the current page should not be open the same time as other pages
                                if(isKeyOf(UNIQUE_PAGES, page)) {
                                    let error: boolean | string = false;
                                    let pageName: false | string = false;

                                    // Check if the current page is already open
                                    if(isKeyOf(pages, page)) {
                                        if(page === "/") {
                                            error = `Your ${UNIQUE_PAGES[page]} is already open in another tab or window`;
                                        } else {
                                            error = `The ${UNIQUE_PAGES[page]} page is already open in another tab or window`;
                                        }

                                        pageName = page;
                                    } else {
                                        // Check to see if another unique page is open
                                        const alreadyOpen = openPaths.find(path => isKeyOf(UNIQUE_PAGES, path));

                                        if(alreadyOpen) {
                                            error = `Cannot open "${UNIQUE_PAGES[page]}" when "${UNIQUE_PAGES[alreadyOpen as keyof typeof UNIQUE_PAGES]}" is open`;
                                            pageName = alreadyOpen;
                                        }
                                    }

                                    if(error) {
                                        this.setState({
                                            loading: false,
                                            page   : pageName,
                                            error,
                                        });

                                        disableResponse();

                                        this.pageInterval = setInterval(this.checkOpenPages, 1000);

                                        rej(error);
                                    } else {
                                        enableResponse();

                                        res(true);
                                    }
                                } else {
                                    enableResponse();

                                    res(true);
                                }
                            }
                        });
                })
                .catch(err => {
                    rej(err);
                });
        });
    };

    public isFirstVisit = (): boolean => {
        let firstVisit = this.props.first;

        if(firstVisit) {
            const lastLoginString = getOption("last_login_time");

            if(lastLoginString) {
                const lastLoginTime = moment(lastLoginString);
                const maxTime = moment().subtract(2, "hour");

                if(lastLoginTime.isAfter(maxTime)) {
                    firstVisit = false;
                }
            }
        }

        return firstVisit;
    };

    public handleRequirePasswordChange = (requirePassword: boolean): void => {
        this.setState({
            password: requirePassword,
        });
    };

    public render(): React.ReactNode {
        return (
            <div id="login-screen" className={(this.state.loading || this.state.error) ? "loading" : ""}>
                <div id="login-screen-background-overlay" />
                <div id="login-screen-flex-child">
                    <ShopfrontLogo />
                    {this.state.error && (
                        <div className="login-error">
                            <span className="text-2xl">{this.state.error}</span>
                            <div className="login-error-actions">
                                <Button onClick={this.handleDashboardClick}>
                                    <FontAwesomeLight icon="tachometer-alt" />
                                    Go to dashboard
                                </Button>
                                <Button onClick={this.handleAttemptOpenPage}>
                                    <FontAwesomeLight icon="window-restore" />
                                    Find &#34;{UNIQUE_PAGES[this.state.page as keyof typeof UNIQUE_PAGES]}&#34; tab
                                </Button>
                            </div>
                        </div>
                    )}
                    {this.state.loading && (
                        <div className="login-loading">
                            <div className="lds-ellipsis">
                                <div />
                                <div />
                                <div />
                                <div />
                            </div>
                        </div>
                    )}
                    {!this.state.error && (
                        <LoginBox
                            mode={this.state.override ? "type" : this.state.mode}
                            requirePassword={this.state.override || this.state.password}
                            loginUser={this.loginCurrentUser}
                            loginOffline={this.loginOfflineUser}
                            users={this.state.users}
                            onRequirePasswordChange={this.handleRequirePasswordChange}
                        />
                    )}
                </div>
                {!this.state.error && this.state.enterpriseLoginVisible && (
                    <EnterpriseLoginBox />
                )}
                {(!this.props.focus && !this.state.error) && <FocusLost />}
            </div>
        );
    }
}

export const LoginScreen: FC = () => {
    const first = useShopfrontSelector(state => state.information.firstVisit);
    const focus = useShopfrontSelector(state => state.layout.focus);

    const dispatch = useShopfrontDispatch();
    const createDialog = useDialogCreate();

    return (
        <LoginScreenRenderer
            first={first}
            focus={focus}
            dispatch={dispatch}
            createDialog={createDialog}
        />
    );
};
